/**
 * Schema.org structured data generator for Madow Tech
 *
 * This utility helps create structured data in JSON-LD format for the software agency website,
 * improving SEO and enabling rich results in search engines.
 */

/**
 * Generate JSON-LD for Madow Tech Organization
 */
export function generateOrganizationSchema(options?: {
  name?: string;
  url?: string;
  logo?: string;
  description?: string;
  foundingDate?: string;
  founder?: string;
  location?: string;
  sameAs?: string[];
  knowsAbout?: string[];
}): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': 'https://madow.tech/#organization',
    name: options?.name || 'Madow Tech',
    alternateName: 'Madow',
    url: options?.url || 'https://madow.tech',
    logo: {
      '@type': 'ImageObject',
      url: options?.logo || 'https://madow.tech/web-app-manifest-512x512.png',
      width: 512,
      height: 512,
    },
    description:
      options?.description ||
      'A young team of experienced developers and designers building web platforms, mobile apps, and digital products that actually work. From Argentina to the world.',
    foundingDate: options?.foundingDate || '2024',
    founder: {
      '@type': 'Person',
      name: options?.founder || 'Marcelo Mira',
      alternateName: 'Mars',
      url: 'https://marcemira.com',
      sameAs: [
        'https://www.linkedin.com/in/marcemira',
        'https://github.com/marcemira',
      ],
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: options?.location || 'Córdoba',
      addressCountry: 'AR',
    },
    areaServed: [
      { '@type': 'Country', name: 'Argentina' },
      { '@type': 'Country', name: 'United States' },
      { '@type': 'Continent', name: 'Europe' },
      { '@type': 'Continent', name: 'Latin America' },
    ],
    sameAs: options?.sameAs || [
      'https://www.linkedin.com/company/madow-tech',
      'https://github.com/madow-tech',
    ],
    knowsAbout: options?.knowsAbout || [
      'Web Development',
      'Mobile App Development',
      'TypeScript',
      'JavaScript',
      'Ember.js',
      'React',
      'NestJS',
      'Node.js',
      'PostgreSQL',
      'Docker',
      'UI/UX Design',
      'Team Augmentation',
      'Software Consulting',
    ],
    slogan: "Let's build scalable web & mobile apps!",
    numberOfEmployees: {
      '@type': 'QuantitativeValue',
      minValue: 5,
      maxValue: 15,
    },
  };
}

/**
 * Generate JSON-LD for the website
 */
export function generateWebsiteSchema(options?: {
  name?: string;
  url?: string;
  description?: string;
  inLanguage?: string[];
}): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': 'https://madow.tech/#website',
    name: options?.name || 'Madow Tech',
    url: options?.url || 'https://madow.tech',
    description:
      options?.description ||
      'Software factory building web platforms, mobile apps, and digital products. From Argentina to the world.',
    publisher: {
      '@id': 'https://madow.tech/#organization',
    },
    inLanguage: options?.inLanguage || ['en', 'es'],
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: 'https://madow.tech/?q={search_term_string}',
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

/**
 * Generate JSON-LD for a Service offering
 */
export function generateServiceSchema(options: {
  name: string;
  description: string;
  serviceType?: string;
  areaServed?: string[];
}): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: options.name,
    description: options.description,
    provider: {
      '@id': 'https://madow.tech/#organization',
    },
    serviceType: options.serviceType || 'Software Development',
    areaServed: options.areaServed || ['Worldwide'],
  };
}

/**
 * Generate JSON-LD for all Madow Tech services
 */
export function generateServicesSchema(): Record<string, unknown> {
  const services = [
    {
      name: 'Team Augmentation',
      description:
        'Plug in experienced engineers who work like your own team. Transparent collaboration, senior-level code quality, and flexible scaling.',
      serviceType: 'Staff Augmentation',
    },
    {
      name: 'End-to-End Delivery',
      description:
        'From concept to launch, we handle it all: UX/UI design, frontend and backend development, CI/CD setup, and deployment.',
      serviceType: 'Custom Software Development',
    },
    {
      name: 'QA & Testing',
      description:
        'Automated and manual testing, performance audits, and reliable release cycles. We break it before users do.',
      serviceType: 'Quality Assurance',
    },
    {
      name: 'Custom Software Development',
      description:
        'Complex ideas deserve clean code. API-driven architecture, SaaS platforms, and long-term support.',
      serviceType: 'Custom Software Development',
    },
    {
      name: 'Mobile & Desktop Development',
      description:
        'Responsive web apps, hybrid builds, and smooth UX on any device. Your product everywhere.',
      serviceType: 'Mobile App Development',
    },
  ];

  return {
    '@context': 'https://schema.org',
    '@type': 'OfferCatalog',
    name: 'Madow Tech Services',
    itemListElement: services.map((service, index) => ({
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Service',
        name: service.name,
        description: service.description,
        serviceType: service.serviceType,
        provider: {
          '@id': 'https://madow.tech/#organization',
        },
      },
      position: index + 1,
    })),
  };
}

/**
 * Generate JSON-LD for a Review/Testimonial
 */
export function generateReviewSchema(options: {
  author: string;
  reviewBody: string;
  datePublished?: string;
  ratingValue?: number;
}): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Review',
    author: {
      '@type': 'Person',
      name: options.author,
    },
    reviewBody: options.reviewBody,
    datePublished: options.datePublished || new Date().toISOString().split('T')[0],
    reviewRating: {
      '@type': 'Rating',
      ratingValue: options.ratingValue || 5,
      bestRating: 5,
      worstRating: 1,
    },
    itemReviewed: {
      '@id': 'https://madow.tech/#organization',
    },
  };
}

/**
 * Generate JSON-LD for all testimonials
 */
export function generateTestimonialsSchema(): Record<string, unknown> {
  const testimonials = [
    {
      author: 'Axel, Engineering Lead @ ID Plans',
      reviewBody:
        'Madow delivered clean, on-spec code ahead of every deadline. Professional execution that kept our timelines tight and quality high.',
      datePublished: '2024-12-01',
    },
    {
      author: 'Nicolás Romagnoli, Co-Founder @ Feldico',
      reviewBody:
        "Trusted Marcelo's team from day one. His 3.5 years scaling Outdoorsy brought proven strategies to our marketplace. Results: faster launches, smarter features.",
      datePublished: '2025-09-01',
    },
  ];

  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': 'https://madow.tech/#organization',
    review: testimonials.map((t) => ({
      '@type': 'Review',
      author: {
        '@type': 'Person',
        name: t.author,
      },
      reviewBody: t.reviewBody,
      datePublished: t.datePublished,
      reviewRating: {
        '@type': 'Rating',
        ratingValue: 5,
        bestRating: 5,
        worstRating: 1,
      },
    })),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: 5,
      reviewCount: testimonials.length,
      bestRating: 5,
      worstRating: 1,
    },
  };
}

/**
 * Generate JSON-LD for a Team Member
 */
export function generateTeamMemberSchema(options: {
  name: string;
  jobTitle: string;
  description?: string;
  image?: string;
  sameAs?: string[];
}): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: options.name,
    jobTitle: options.jobTitle,
    description: options.description,
    image: options.image,
    worksFor: {
      '@id': 'https://madow.tech/#organization',
    },
    sameAs: options.sameAs || [],
  };
}

/**
 * Generate JSON-LD for breadcrumb navigation
 */
export function generateBreadcrumbSchema(
  items: Array<{ name: string; url: string }>
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

/**
 * Generate JSON-LD for the WebPage
 */
export function generateWebPageSchema(options: {
  name: string;
  description: string;
  url: string;
  inLanguage?: string;
}): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${options.url}#webpage`,
    name: options.name,
    description: options.description,
    url: options.url,
    isPartOf: {
      '@id': 'https://madow.tech/#website',
    },
    about: {
      '@id': 'https://madow.tech/#organization',
    },
    inLanguage: options.inLanguage || 'en',
  };
}

/**
 * Generate JSON-LD for LocalBusiness (for local SEO)
 */
export function generateLocalBusinessSchema(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    '@id': 'https://madow.tech/#localbusiness',
    name: 'Madow Tech',
    image: 'https://madow.tech/web-app-manifest-512x512.png',
    url: 'https://madow.tech',
    telephone: '',
    priceRange: '$$',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Córdoba',
      addressCountry: 'AR',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: -31.4201,
      longitude: -64.1888,
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '18:00',
    },
    sameAs: [
      'https://www.linkedin.com/company/madow-tech',
      'https://github.com/madow-tech',
    ],
  };
}

/**
 * Generate complete structured data for the homepage
 */
export function generateHomePageStructuredData(locale: string = 'en'): string {
  const graph = [
    generateOrganizationSchema(),
    generateWebsiteSchema({ inLanguage: [locale, locale === 'en' ? 'es' : 'en'] }),
    generateWebPageSchema({
      name: "Madow Tech - Let's build scalable web & mobile apps!",
      description:
        'A young team of experienced developers and designers building web platforms, mobile apps, and digital products that actually work.',
      url: `https://madow.tech/${locale}`,
      inLanguage: locale,
    }),
    generateServicesSchema(),
  ];

  return JSON.stringify({
    '@context': 'https://schema.org',
    '@graph': graph.map((item) => {
      // Remove @context from nested items as we're using @graph
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { '@context': _, ...rest } = item;
      return rest;
    }),
  });
}

export default {
  generateOrganizationSchema,
  generateWebsiteSchema,
  generateServiceSchema,
  generateServicesSchema,
  generateReviewSchema,
  generateTestimonialsSchema,
  generateTeamMemberSchema,
  generateBreadcrumbSchema,
  generateWebPageSchema,
  generateLocalBusinessSchema,
  generateHomePageStructuredData,
};
